/**
 * A-02 — business overview: sales against the previous period, the sales trend, the order funnel
 * and fulfilment times, the top-selling medicines (→ the top 100 and any medicine's rank) and top
 * customers, and the live order-status and digitisation queues.
 *
 * The period (?days=, see lib/kpi PERIODS) scopes every card but the live queues and is kept in
 * the URL, so the top-100 page and the back button keep it. Switching it keeps each card's
 * figures, dimmed, until the new ones arrive — no skeleton flash, no layout jump.
 */

import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  kpiDashboard,
  topCustomers,
  topMedicines,
  type KpiDashboard as Kpi,
  type KpiSeriesPoint,
  type SeriesBucket,
} from "../../api/analytics";
import { describeError } from "../../api/errors";
import { ColumnChart, type ColumnBar } from "../../components/kpi/ColumnChart";
import {
  Card,
  ChangeBadge,
  LoadError,
  RankMoveBadge,
  Segmented,
  ShareBar,
  Skeleton,
  StatTile,
} from "../../components/kpi/KpiParts";
import { DigitiseQueueCard, OrderQueuesCard } from "../../components/kpi/QueueCards";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";
import { ExportButton } from "../../components/ui/ExportButton";
import { CUSTOMER_RANK_EXPORT, MEDICINE_RANK_EXPORT, salesSeriesExport } from "../../lib/kpiExports";
import {
  change,
  chunkFromEnd,
  dayLabel,
  formatCompact,
  formatHours,
  formatNumber,
  formatPercent,
  PERIODS,
  pointsLabel,
  previousLabel,
  rangeLabel,
  runLabel,
  runLength,
  wallClock,
} from "../../lib/kpi";
import { formatINR, formatINRCompact } from "../../lib/money";
import { timeAgo } from "../../lib/uploadQueue";
import { useKpiPeriod } from "../../lib/useKpiPeriod";
import { useNow } from "../../lib/useNow";

const PERIOD_OPTIONS = PERIODS.map((p) => ({ value: p.days, label: p.label }));
const TOP_ROWS = 10;

export function KpiDashboard() {
  const [days, setDays] = useKpiPeriod();
  const qc = useQueryClient();
  const now = useNow(30_000);
  const kpi = useQuery({ queryKey: ["kpi", "dashboard", days], queryFn: () => kpiDashboard(days), placeholderData: keepPreviousData });
  const [refreshing, setRefreshing] = useState(false);
  const k = kpi.data;

  const refresh = async () => {
    setRefreshing(true);
    try {
      await qc.refetchQueries({ queryKey: ["kpi"], type: "active" });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="page">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h2 className="page-title">Business overview</h2>
          <p className="page-subtitle">{k ? periodCaption(k) : "Sales, orders and queues"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={refreshing}
            className="btn-secondary btn-sm"
            aria-label="Refresh"
            title={kpi.dataUpdatedAt ? `Updated ${timeAgo(new Date(kpi.dataUpdatedAt).toISOString(), now)}` : undefined}
          >
            <RefreshIcon spinning={refreshing} />
            {/* Icon only on a phone, so the period picker and this share one line. */}
            <span className="hidden sm:inline">{refreshing ? "Refreshing…" : "Refresh"}</span>
          </button>
        </div>
      </div>

      {kpi.isLoading ? (
        <HeadlineSkeleton />
      ) : !k ? (
        <LoadError message={describeError(kpi.error)} onRetry={() => void kpi.refetch()} />
      ) : (
        <>
          <Headline k={k} busy={kpi.isPlaceholderData} />
          <div className="grid gap-4 lg:grid-cols-3">
            <SalesCard k={k} busy={kpi.isPlaceholderData} className="lg:col-span-2" />
            <FunnelCard k={k} busy={kpi.isPlaceholderData} />
          </div>
        </>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <TopMedicinesCard days={days} />
        <TopCustomersCard days={days} />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <OrderQueuesCard />
        <DigitiseQueueCard days={days} />
      </div>
    </div>
  );
}

function periodCaption(k: Kpi): string {
  if (k.window_days === 1) return `Today so far, ${dayLabel(wallClock(k.period_end).date)} · compared with yesterday up to this time`;
  return `${rangeLabel(k.period_start, k.period_end)} · compared with ${previousLabel(k.window_days)}`;
}

// ------------------------------------------------------------------ headline figures

function Headline({ k, busy }: { k: Kpi; busy: boolean }) {
  const p = k.previous;
  const vs = previousLabel(k.window_days);
  const n = (v: string) => Number(v);
  const anyPayments = k.payment_attempts > 0;
  return (
    <div className={`grid grid-cols-2 gap-3 transition-opacity md:grid-cols-4 ${busy ? "opacity-60" : ""}`} aria-busy={busy || undefined}>
      <StatTile hero label="Net revenue" value={formatINRCompact(k.net_revenue)} exact={formatINR(k.net_revenue)}>
        <ChangeBadge change={change(n(k.net_revenue), n(p.net_revenue))} vs={vs} />
        <p className="mt-1 text-xs text-ink-muted">
          {formatINRCompact(k.gross_revenue)} gross · {formatINRCompact(k.refunded_amount)} refunded
        </p>
      </StatTile>
      <StatTile label="Orders placed" value={formatCompact(k.orders_total)} exact={formatNumber(k.orders_total)}>
        <ChangeBadge change={change(k.orders_total, p.orders_total)} vs={vs} />
      </StatTile>
      <StatTile label="Paid orders" value={formatCompact(k.paid_orders)} exact={formatNumber(k.paid_orders)}>
        <ChangeBadge change={change(k.paid_orders, p.paid_orders)} vs={vs} />
      </StatTile>
      <StatTile
        label="Avg order value"
        value={k.paid_orders ? formatINRCompact(k.average_order_value) : "—"}
        exact={k.paid_orders ? formatINR(k.average_order_value) : undefined}
      >
        {k.paid_orders > 0 && <ChangeBadge change={change(n(k.average_order_value), n(p.average_order_value))} vs={vs} />}
      </StatTile>
      <StatTile label="Customers" value={formatCompact(k.customers)} exact={formatNumber(k.customers)}>
        <ChangeBadge change={change(k.customers, p.customers)} vs={vs} />
        <p className="mt-1 text-xs text-ink-muted">
          {formatNumber(k.new_customers)} new · {formatNumber(k.customers - k.new_customers)} returning
        </p>
      </StatTile>
      <StatTile label="Units sold" value={formatCompact(k.units_sold)} exact={formatNumber(k.units_sold)}>
        <ChangeBadge change={change(k.units_sold, p.units_sold)} vs={vs} />
      </StatTile>
      <StatTile label="Payment success" value={anyPayments ? formatPercent(k.payment_success_rate) : "—"}>
        {anyPayments && (
          <ChangeBadge
            change={change(k.payment_success_rate, p.payment_success_rate)}
            label={pointsLabel(k.payment_success_rate, p.payment_success_rate)}
            vs={vs}
          />
        )}
        <p className="mt-1 text-xs text-ink-muted">{formatNumber(k.payment_attempts)} payment attempts</p>
      </StatTile>
    </div>
  );
}

function HeadlineSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className={`card p-4 ${i === 0 ? "col-span-2" : ""}`}>
          <Skeleton className="h-3 w-20" />
          <Skeleton className={`mt-3 ${i === 0 ? "h-11 w-40" : "h-7 w-24"}`} />
          <Skeleton className="mt-3 h-3 w-28" />
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ sales trend

/** "14:00 – 16:00" for a run of hours (each point is the hour it starts). */
function hourRange(starts: string[]): string {
  const from = wallClock(starts[0]).time;
  const lastHour = Number(wallClock(starts[starts.length - 1]).time.slice(0, 2)) + 1;
  return `${from} – ${String(lastHour).padStart(2, "0")}:00`;
}

/** One bar per point, or per run of `size` points (counted back from today). */
function salesBars(points: KpiSeriesPoint[], bucket: SeriesBucket, size = 1): ColumnBar[] {
  return chunkFromEnd(points, size).map((run) => {
    const starts = run.map((p) => p.start);
    const revenue = run.reduce((sum, p) => sum + Number(p.revenue), 0);
    const paid = run.reduce((sum, p) => sum + p.paid_orders, 0);
    const placed = run.reduce((sum, p) => sum + p.orders, 0);
    const last = wallClock(starts[starts.length - 1]).date;
    return {
      key: starts[0],
      label: bucket === "hour" ? wallClock(starts[0]).time : dayLabel(last),
      title: bucket === "hour" ? hourRange(starts) : runLabel(starts, bucket),
      value: revenue,
      valueText: formatINR(revenue),
      details: [
        { label: "paid orders", value: formatNumber(paid) },
        { label: "orders placed", value: formatNumber(placed) },
      ],
    };
  });
}

function SalesCard({ k, busy, className }: { k: Kpi; busy: boolean; className?: string }) {
  const [asTable, setAsTable] = useState(false);
  const bars = salesBars(k.series, k.series_bucket);
  const peak = bars.reduce<ColumnBar | null>((best, b) => (best === null || b.value > best.value ? b : best), null);
  const per = k.series_bucket === "hour" ? "hour" : "day";
  const summary =
    `Gross sales per ${per}, ${rangeLabel(k.period_start, k.period_end)}: ${formatINR(k.gross_revenue)} in all` +
    (peak && peak.value > 0 ? `, highest ${formatINRCompact(peak.value)} (${peak.title}).` : ".");
  const buckets = Math.max(1, k.series.length);
  return (
    <Card
      title="Sales"
      subtitle={`What orders paid each ${per} came to · ${formatNumber(k.paid_orders)} paid orders`}
      busy={busy}
      className={className}
      bodyClassName="flex flex-col gap-4"
      action={
        <span className="flex flex-wrap items-center gap-1">
          <ExportButton table={`${per === "hour" ? "hourly" : "daily"} sales`} columns={salesSeriesExport(k.series_bucket)} rows={k.series} />
          <button type="button" onClick={() => setAsTable((t) => !t)} aria-pressed={asTable} className="btn-ghost btn-sm">
            {asTable ? "Show chart" : "Show table"}
          </button>
        </span>
      }
    >
      <ColumnChart
        bars={bars}
        seriesLabel="sales"
        axisFormat={formatINRCompact}
        asTable={asTable}
        summary={summary}
        fill
        height={220}
        className="flex-1"
        regroup={(maxBars) => salesBars(k.series, k.series_bucket, runLength(k.series.length, maxBars))}
      />
      <dl className="grid shrink-0 grid-cols-3 gap-2 border-t border-line pt-3 text-sm">
        <div className="min-w-0">
          <dt className="truncate text-xs text-ink-muted">Average per {per}</dt>
          <dd className="truncate font-semibold text-ink" title={formatINR(Number(k.gross_revenue) / buckets)}>
            {formatINRCompact(Number(k.gross_revenue) / buckets)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="truncate text-xs text-ink-muted">Best {per}</dt>
          <dd className="truncate font-semibold text-ink">
            {peak && peak.value > 0 ? (
              <>
                {formatINRCompact(peak.value)} <span className="font-normal text-ink-muted">· {peak.title}</span>
              </>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="truncate text-xs text-ink-muted">Paid orders per {per}</dt>
          <dd className="truncate font-semibold text-ink">{formatNumber(Math.round((k.paid_orders / buckets) * 10) / 10)}</dd>
        </div>
      </dl>
    </Card>
  );
}

// ------------------------------------------------------------------ funnel + fulfilment

const STAGES = [
  { key: "placed", label: "Placed" },
  { key: "priced", label: "Priced" },
  { key: "paid", label: "Paid" },
  { key: "delivered", label: "Delivered" },
] as const;
// An ordinal ramp: one hue, darker for the earlier stage (validated light-end contrast 4.2:1).
const STAGE_FILL = ["bg-brand-700", "bg-brand-600", "bg-brand-500", "bg-brand-400"];

const statusLabel = (code: string) => {
  const words = code.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

function FunnelCard({ k, busy }: { k: Kpi; busy: boolean }) {
  const placed = k.funnel.placed ?? 0;
  const statuses = Object.entries(k.orders_by_status).sort(([, a], [, b]) => b - a);
  return (
    <Card title="Order funnel" subtitle="Orders placed in the period, by how far they got" busy={busy}>
      <ul className="space-y-3">
        {STAGES.map((stage, i) => {
          const value = k.funnel[stage.key] ?? 0;
          const share = placed ? value / placed : 0;
          return (
            <li key={stage.key}>
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-ink-soft">{stage.label}</span>
                <span className="tabular-nums">
                  <strong className="font-semibold text-ink">{formatNumber(value)}</strong>
                  <span className="ml-1.5 text-xs text-ink-muted">{placed ? formatPercent(share, 0) : "—"}</span>
                </span>
              </div>
              <div className="mt-1 h-2.5 w-full">
                <div className={`h-2.5 rounded-r-sm ${STAGE_FILL[i]}`} style={{ width: `${share * 100}%`, minWidth: value ? 2 : 0 }} />
              </div>
            </li>
          );
        })}
      </ul>

      <h4 className="section-title mt-6 text-xs">Median time</h4>
      <dl className="mt-2 grid grid-cols-3 gap-2 text-sm">
        {(
          [
            ["To price", k.fulfilment.median_hours_to_price],
            ["To pay", k.fulfilment.median_hours_to_pay],
            ["To deliver", k.fulfilment.median_hours_to_deliver],
          ] as const
        ).map(([label, hours]) => (
          <div key={label} className="rounded-xl border border-line bg-raised px-2.5 py-2">
            <dt className="text-xs text-ink-muted">{label}</dt>
            <dd className="mt-0.5 font-semibold text-ink">{formatHours(hours)}</dd>
          </div>
        ))}
      </dl>

      {statuses.length > 0 && (
        <>
          <h4 className="section-title mt-6 text-xs">Where they are now</h4>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {statuses.map(([code, count]) => (
              <li key={code}>
                <OrderStatusBadge code={code} label={`${statusLabel(code)} · ${formatNumber(count)}`} />
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ rankings

function RankCell({ rank, previous }: { rank: number; previous: number | null }) {
  return (
    <td className="w-14 py-2 pr-2 align-middle">
      <span className="flex items-center gap-1.5">
        <span className="w-5 text-right text-sm font-semibold tabular-nums text-ink">{rank}</span>
        <RankMoveBadge rank={rank} previous={previous} />
      </span>
    </td>
  );
}

const TH = "pb-2 text-xs font-medium text-ink-muted";

function TopMedicinesCard({ days }: { days: number }) {
  const q = useQuery({
    queryKey: ["kpi", "top-medicines", days, "units", TOP_ROWS],
    queryFn: () => topMedicines(days, "units", TOP_ROWS),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const topUnits = d?.rows[0]?.units_sold ?? 0;
  return (
    <Card
      title="Top-selling medicines"
      subtitle={d ? `By units sold · ${formatNumber(d.ranked_medicines)} medicines sold in the period` : "By units sold"}
      busy={q.isPlaceholderData}
      action={
        <span className="flex flex-wrap items-center gap-2">
          {/* The top 100 of the period, not just the 10 shown. */}
          <ExportButton
            table="top medicines"
            label="Export top 100"
            columns={MEDICINE_RANK_EXPORT}
            fetchPage={async () => ({ rows: (await topMedicines(days, "units", 100)).rows, next: null })}
          />
          <Link to={`/staff/admin/kpi/top-medicines?days=${days}`} className="link whitespace-nowrap text-sm">
            Top 100 &amp; find a medicine →
          </Link>
        </span>
      }
    >
      {q.isLoading ? (
        <RowsSkeleton />
      ) : !d ? (
        <LoadError message={describeError(q.error)} onRetry={() => void q.refetch()} />
      ) : d.rows.length === 0 ? (
        <p className="empty-state py-8">No medicines sold in this period.</p>
      ) : (
        <table className="w-full table-fixed text-sm">
          <caption className="sr-only">Top {d.rows.length} medicines by units sold, with rank changes from the previous period</caption>
          <thead>
            <tr className="text-left">
              <th scope="col" className={`${TH} w-14`}>#</th>
              <th scope="col" className={TH}>Medicine</th>
              <th scope="col" className={`${TH} w-16 text-right`}>Units</th>
              <th scope="col" className={`${TH} hidden w-24 text-right sm:table-cell`}>Revenue</th>
              <th scope="col" className={`${TH} hidden w-28 pl-4 md:table-cell`}>
                <span className="sr-only">Units compared with the top seller</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {d.rows.map((m) => (
              <tr key={m.medicine_id}>
                <RankCell rank={m.rank} previous={m.previous_rank} />
                <td className="min-w-0 py-2">
                  <Link
                    to={`/staff/admin/kpi/top-medicines?days=${days}&medicine=${m.medicine_id}`}
                    className="block truncate font-medium text-ink hover:text-brand hover:underline"
                    title={`${m.name} — see its analytics`}
                  >
                    {m.name}
                  </Link>
                  {m.brand && <span className="block truncate text-xs text-ink-muted">{m.brand}</span>}
                </td>
                <td className="py-2 text-right font-semibold tabular-nums text-ink">{formatNumber(m.units_sold)}</td>
                <td className="hidden py-2 text-right tabular-nums text-ink-soft sm:table-cell" title={formatINR(m.revenue)}>
                  {formatINRCompact(m.revenue)}
                </td>
                <td className="hidden py-2 pl-4 md:table-cell">
                  <ShareBar fraction={topUnits ? m.units_sold / topUnits : 0} label={`${formatPercent(m.unit_share)} of units sold`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function TopCustomersCard({ days }: { days: number }) {
  const now = useNow();
  const q = useQuery({
    queryKey: ["kpi", "top-customers", days, "spend", TOP_ROWS],
    queryFn: () => topCustomers(days, "spend", TOP_ROWS),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const topSpend = Number(d?.rows[0]?.spend ?? 0);
  return (
    <Card
      title="Top customers"
      subtitle={d ? `By spend · ${formatNumber(d.customers)} customers bought in the period` : "By spend"}
      busy={q.isPlaceholderData}
      action={
        <ExportButton
          table="top customers"
          label="Export top 100"
          columns={CUSTOMER_RANK_EXPORT}
          fetchPage={async () => ({ rows: (await topCustomers(days, "spend", 100)).rows, next: null })}
        />
      }
    >
      {q.isLoading ? (
        <RowsSkeleton />
      ) : !d ? (
        <LoadError message={describeError(q.error)} onRetry={() => void q.refetch()} />
      ) : d.rows.length === 0 ? (
        <p className="empty-state py-8">No purchases in this period.</p>
      ) : (
        <table className="w-full table-fixed text-sm">
          <caption className="sr-only">Top {d.rows.length} customers by spend</caption>
          <thead>
            <tr className="text-left">
              <th scope="col" className={`${TH} w-10`}>#</th>
              <th scope="col" className={TH}>Customer</th>
              <th scope="col" className={`${TH} hidden w-16 text-right sm:table-cell`}>Orders</th>
              <th scope="col" className={`${TH} w-24 text-right`}>Spend</th>
              <th scope="col" className={`${TH} hidden w-28 pl-4 md:table-cell`}>
                <span className="sr-only">Spend compared with the top customer</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {d.rows.map((c) => (
              <tr key={c.user_id}>
                <td className="py-2 pr-2 text-sm font-semibold tabular-nums text-ink">{c.rank}</td>
                <td className="min-w-0 py-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-medium text-ink">{c.name}</span>
                    {c.is_new && <span className="badge-info shrink-0 px-1.5 py-0 text-[10px]">New</span>}
                  </span>
                  <span className="block truncate text-xs text-ink-muted">
                    {c.phone_masked ?? "No phone"}
                    <span className="sm:hidden"> · {formatNumber(c.orders)} orders</span>
                    <span className="hidden sm:inline"> · last {timeAgo(c.last_order_at, now)}</span>
                  </span>
                </td>
                <td className="hidden py-2 text-right tabular-nums text-ink-soft sm:table-cell">{formatNumber(c.orders)}</td>
                <td className="py-2 text-right font-semibold tabular-nums text-ink" title={formatINR(c.spend)}>
                  {formatINRCompact(c.spend)}
                </td>
                <td className="hidden py-2 pl-4 md:table-cell">
                  <ShareBar
                    fraction={topSpend ? Number(c.spend) / topSpend : 0}
                    label={`${formatPercent(Number(d.total_spend) ? Number(c.spend) / Number(d.total_spend) : 0)} of all spend`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function RowsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-6" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-12" />
        </div>
      ))}
    </div>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} aria-hidden>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}
